import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './CategoryPage.css';

const CategoryPage = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPriceAccess, setHasPriceAccess] = useState(false);
  const { customer } = useCustomerAuth();

  useEffect(() => {
    if (id) {
      fetchCategoryAndProducts();
    }
  }, [id, currentPage]);

  const fetchCategoryAndProducts = async () => {
    try {
      setLoading(true);

      // Fetch category info
      const categoryResponse = await fetch('/api/store/categories');
      const categories = await categoryResponse.json();
      const currentCategory = categories.find(cat => cat.id == id);
      setCategory(currentCategory);

      // Fetch products for this category
      const token = localStorage.getItem('customerToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const productsResponse = await fetch(
        `/api/store/categories/${id}/products?page=${currentPage}&limit=12`,
        { headers }
      );
      const data = await productsResponse.json();
      setProducts(data.products);
      setTotalPages(data.pagination.pages);
      setHasPriceAccess(data.hasPriceAccess || false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching category data:', error);
      setLoading(false);
    }
  };



  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="category-page-loading">
        <div className="loading-spinner"></div>
        <p>Cargando categoría...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="category-page-error">
        <h2>Categoría no encontrada</h2>
        <Link to="/tienda" className="back-btn">
          Volver a la tienda
        </Link>
      </div>
    );
  }

  return (
    <div className="category-page-container">
      {/* Category Header - Full Width */}
      <div className="category-header">
        <h1>{category.name}</h1>
        {category.description && (
          <p className="category-description">{category.description}</p>
        )}
      </div>

      <div className="category-page-content">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/tienda">Tienda</Link>
          <span> / </span>
          <span>{category.name}</span>
        </nav>



        {/* Products Count */}
        <div className="products-count">
          <p>
            {products.length} productos en {category.name}
          </p>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="no-products">
            <p>
              No hay productos disponibles en {category.name}
            </p>
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.map((product) => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0].image_url}
                        alt={product.short_title}
                        onError={(e) => {
                          e.target.src = '/assets/images/Distracker.png';
                        }}
                      />
                    ) : (
                      <img src="/assets/images/Distracker.png" alt="Default" />
                    )}
                  </div>
                  <div className="store-product-info">
                    <h3 className="product-title">{product.short_title}</h3>
                    <p className="product-category">{product.category_name}</p>
                    {hasPriceAccess && product.price !== null && (
                      <p className="product-price">{formatPrice(product.price)}</p>
                    )}
                    <Link to={`/tienda/producto/${product.id}`} className="view-product-btn">
                      Ver producto
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Anterior
                </button>

                <span className="pagination-info">
                  Página {currentPage} de {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
